import { SearchContextQuery } from "@/core/context/__generated__/SearchContextQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import Relay from "@/relay/relayUtils"
import SearchModal from "@/search/SearchModal"
import { SearchType, SearchTypes } from "@/search/utils/searchUtils"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import useOnKeydown from "@utils/hook/useOnKeydown"
import useSubscriptionStanding from "@utils/hook/useSubscriptionStanding"
import { camelize } from "inflected"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import {
  InstantSearch,
  useCurrentRefinements,
  useRefinementList,
  useSearchBox,
} from "react-instantsearch-hooks-web"
import { graphql } from "relay-runtime"
import TypesenseInstantSearchAdapter, {
  SearchClient,
  TypesenseInstantsearchAdapterOptions,
} from "typesense-instantsearch-adapter"

export type SearchClientContext = {
  searchClient: SearchClient | null
  indexName: string
  refreshSearch: () => void
  searchOptOuts: Set<string>
}

export const SearchContext = createContext<
  Partial<SearchClientContext> & {
    query: string
    refine: (value: string) => void
    isSearchOpen: boolean
    openSearch?: () => void
    closeSearch?: () => void
    filter: (value: string) => void
    appliedFilters: SearchType[]
  }
>({
  query: "",
  refine: (value: string) => value,
  isSearchOpen: false,
  appliedFilters: [],
  filter: (value: string) => value,
})

export function useSearchContext() {
  return useContext(SearchContext)
}

interface SearchProviderProps {
  children: ReactNode
  search?: SearchClientContext
}

function SearchProvider({ children }: SearchProviderProps) {
  const activeOrganization = useActiveOrganization()
  const { globalSearch } = useFeatureFlags()
  const subscriptionStanding = useSubscriptionStanding()

  const [{ organization }, refetch] =
    Relay.useRefetchableBackgroundQuery<SearchContextQuery>(
      graphql`
        query SearchContextQuery($id: ID!) {
          organization: node(id: $id) {
            ... on Organization {
              id
              searchConfig
              viewerMembership {
                searchOptOuts {
                  edges {
                    node {
                      id
                      role
                      entity
                    }
                  }
                }
              }
            }
          }
        }
      `,
      { id: activeOrganization?.id || "" },
      { fetchPolicy: "store-or-network" }
    )

  const searchConfig = organization?.searchConfig
  const searchOptOuts = new Set(
    Relay.connectionToArray(organization?.viewerMembership?.searchOptOuts).map((optOut) =>
      camelize(optOut.entity, false)
    )
  )
  const allEntitiesOptedOutOf = searchOptOuts.size === SearchTypes.length

  const { searchClient } = useMemo(() => {
    if (!searchConfig) return { searchClient: null }
    if (allEntitiesOptedOutOf) return { searchClient: null }
    return new TypesenseInstantSearchAdapter(
      JSON.parse(searchConfig) as TypesenseInstantsearchAdapterOptions
    )
  }, [searchConfig, allEntitiesOptedOutOf])

  if (
    !globalSearch ||
    !organization ||
    !searchClient ||
    allEntitiesOptedOutOf ||
    subscriptionStanding !== "active"
  ) {
    return <>{children}</>
  }

  const indexName = Relay.fromGlobalId(activeOrganization?.id || "").id

  const refreshSearch = () => {
    refetch({ id: activeOrganization?.id || "" })
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <SearchProviderWithClient
        search={{
          searchClient,
          indexName,
          refreshSearch,
          searchOptOuts,
        }}
      >
        {children}
      </SearchProviderWithClient>
    </InstantSearch>
  )
}

function SearchProviderWithClient({ children, search }: SearchProviderProps) {
  const { globalSearch } = useFeatureFlags()
  const activeOrganization = useActiveOrganization()
  const { query, refine } = useSearchBox()
  const isMobile = useIsMobile()
  const { refine: filter } = useRefinementList({ attribute: "type" })
  const { items: currentRefinements } = useCurrentRefinements()
  const appliedFilters = currentRefinements
    ?.map((cr) => cr.refinements.map((r: any) => (r as any).value as SearchType))
    .flat()

  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const canSearch = Boolean(activeOrganization?.viewerMembership && globalSearch)

  const openSearch = useCallback(() => {
    setIsSearchOpen(true)
  }, [])

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false)
  }, [])

  useOnKeydown(
    (e) => {
      if (!isSearchOpen && (e.metaKey || e.ctrlKey) && e.code === "KeyK") {
        e.preventDefault()
        openSearch()
      }
    },
    [isSearchOpen]
  )

  return (
    <SearchContext.Provider
      value={{
        query,
        refine,
        isSearchOpen,
        openSearch,
        closeSearch,
        filter,
        appliedFilters,
        ...search,
      }}
    >
      {children}

      {canSearch && isMobile && (
        <SearchModal
          isOpen={isSearchOpen}
          onClose={closeSearch}
          title={""}
          testid={"SearchButton"}
          modalContentLabel={"SearchModal"}
        />
      )}
    </SearchContext.Provider>
  )
}

export default SearchProvider
